<template>
    <div>
        <div class="upload-wrapper">
            <div class="d-flex justify-content-center align-items-center mb-4 steps-container">
                <div class="step-item step-blue">
                    <span class="step-dot-blue"></span>
                    <span class="step-label-blue">Gerar PDF</span>
                </div>
                <div class="step-line completed"></div>
                <div class="step-item active">
                    <span class="step-dot"></span>
                    <span class="step-label-grey">Anexar Assinado</span>
                </div>
            </div>

            <div class="preview-container" :class="{ 'has-pdf': pdfUrl }">
                <div v-if="pdfUrl" class="pdf-viewer-container">
                    <div class="pdf-file-name" :title="nomeArquivoPreview">
                        {{ nomeArquivoPreview }}
                    </div>
                    <iframe 
                        :src="srcVisualizacaoPdf" 
                        width="100%" 
                        height="600px" 
                        frameborder="0"
                    ></iframe>
                </div>

                <div v-if="carregando" class="loading-overlay">
                    <div class="spinner-container">
                        <i class="fa fa-spinner fa-spin fa-3x text-primary"></i>
                        <p class="mt-3 text-primary font-weight-bold">Gerando PDF...</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import MemoriaCalculoServicos from './MemoriaCalculoServicos'
import download from '@/shared/download'

export default {
    name: 'MemoriaCalculoGerarPdf',
    props: {
        itens: {
            type: Array,
            default: () => []
        },
        boletimMedicao: {
            type: Object,
            default: () => {}
        },
        contrato: {
            type: Object,
            required: true
        },
        valorTotalTodosBoletins: {
            type: Number,
            required: false
        },
        fornecedorLogoUrl: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            file: null,
            carregando: false,
            pdfUrl: null,
            pdfBlob: null
        }
    },
    computed: {
        srcVisualizacaoPdf() {
            if (!this.pdfUrl) return null
            const [urlSemFragmento] = this.pdfUrl.split('#')
            return `${urlSemFragmento}#toolbar=0`
        },
        nomeArquivoPreview() {
            return this.montarNomeArquivoMemoriaCalculo()
        }
    },
    methods: {
        formatarDataPeriodo(data) {
            if (!data) return ''
            const dataFormatada = this.$moment(data)
            if (!dataFormatada.isValid() || dataFormatada.year() <= 1) return ''
            return dataFormatada.format('DD/MM/YYYY')
        },
        obterPeriodoBoletim() {
            const inicio = this.formatarDataPeriodo(this.boletimMedicao.dataInicio)
            const fim = this.formatarDataPeriodo(this.boletimMedicao.dataFim)

            if (inicio && fim) return `${inicio} - ${fim}`
            if (inicio) return inicio
            if (fim) return fim

            return this.boletimMedicao.competencia
                ? `${this.$moment(this.boletimMedicao.competencia).startOf('month').format('DD/MM/YYYY')} - ${this.$moment(this.boletimMedicao.competencia).endOf('month').format('DD/MM/YYYY')}`
                : ''
        },
        normalizarParteNomeArquivo(valor, fallback = '') {
            const texto = (valor || '').toString().trim()
            if (!texto) return fallback

            const semAcento = texto.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            const limpo = semAcento
                .replace(/[\\/:*?"<>|]+/g, '_')
                .replace(/\s+/g, '_')
                .replace(/_+/g, '_')
                .replace(/^[_\.]+|[_\.]+$/g, '')

            return limpo || fallback
        },
        montarNomeArquivoMemoriaCalculo() {
            const nomeBm = this.normalizarParteNomeArquivo(
                this.boletimMedicao?.numero || this.boletimMedicao?.nome,
                'BM'
            )
            const numeroContrato = this.normalizarParteNomeArquivo(
                this.contrato?.codContrato,
                'Contrato'
            )

            return `MemoriaCalculo_${nomeBm}_${numeroContrato}.pdf`
        },
        baixarPdf() {
            if (this.pdfBlob) {
                const nomeArquivo = this.montarNomeArquivoMemoriaCalculo()
                download(this.pdfBlob, nomeArquivo, 'application/pdf')
            }
        },
        extrairNumeroItem(descricao) {
            if (!descricao) return '-';
            const match = descricao.match(/^(\d+(?:\.\d+)*\.?)\s*/);
            return match ? match[1] : '-';
        },
        extrairDescricaoSemNumero(descricao) {
            if (!descricao) return '-';
            return descricao.replace(/^\d+(?:\.\d+)*\.?\s*-?\s*/, '').trim() || descricao;
        },
        async gerarPdf() {
            this.carregando = true

            try {
                const ordenarPorNumero = (a, b) =>
                    (a.descricao || '').localeCompare(b.descricao || '', undefined, { numeric: true });

                const itensTransformados = this.itens
                    .filter(item => item.tipo !== 0)
                    .sort(ordenarPorNumero)
                    .map(item => ({
                        ...item,
                        numeroItem: this.extrairNumeroItem(item.descricao),
                        descricao: this.extrairDescricaoSemNumero(item.descricao),
                        subitens: item.subitens ? item.subitens.slice().sort(ordenarPorNumero).map(sub => ({
                            ...sub,
                            numeroItem: this.extrairNumeroItem(sub.descricao),
                            descricao: this.extrairDescricaoSemNumero(sub.descricao)
                        })) : []
                    }));
                const dto = {
                    imagemEsquerda: 'data:image/png;base64,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',
                    imagemDireita: this.fornecedorLogoUrl ? this.fornecedorLogoUrl : '',
                    itens: itensTransformados,
                    codContrato: this.contrato.codContrato,
                    contratada: this.contrato.fornecedor ? (`${this.contrato.fornecedor.cnpj} - ${this.contrato.fornecedor.nome}`) : '' ,
                    ordemServico: '-',
                    objeto: this.contrato.objeto,
                    medicao: this.boletimMedicao.numero,
                    periodo: this.obterPeriodoBoletim(),
                    valorMedicao: this.boletimMedicao.total,
                    valorContrato: this.contrato.valorTotal ,
                    totalMedio: this.boletimMedicao.total,
                    saldoContratual: (this.contrato.valorTotal - this.valorTotalTodosBoletins) || 0,
                }
                console.log(dto);
                const response = await MemoriaCalculoServicos.gerarPdfMemoriaCalculo(dto)
                
                const blob = new Blob([response], { type: 'application/pdf' })
                
                if (this.pdfUrl) {
                    URL.revokeObjectURL(this.pdfUrl)
                }
                
                this.pdfUrl = URL.createObjectURL(blob)
                
                // Armazenar o response para download posterior
                this.pdfBlob = response

                if (this.$toast) {
                    this.$toast.success('PDF gerado com sucesso!', 'Sucesso', {
                        position: 'topRight',
                        timeout: 3000
                    })
                }
            } catch (error) {
                console.error('Erro ao gerar PDF:', error)
                
                if (this.$toast) {
                    this.$toast.error('Não foi possível gerar o relatório. Por favor, tente novamente.', 'Erro', {
                        position: 'topRight',
                        timeout: 5000
                    })
                } else {
                    alert('Não foi possível gerar o relatório. Por favor, tente novamente.')
                }
            } finally {
                this.carregando = false
            }
        }
    },
    beforeDestroy() {
        if (this.pdfUrl) {
            URL.revokeObjectURL(this.pdfUrl)
        }
        this.pdfBlob = null
    },
    mounted() {
        this.gerarPdf()
    }
}
</script>

<style scoped>
    .upload-wrapper {
    width: 100%;
}

.steps-container { margin-bottom: 30px; }

.step-label-blue {
    font-size: 0.9rem;
    font-weight: 600;
    color: #007bff;
}
.step-label-grey {
    font-size: 0.9rem;
    font-weight: 600;
    color: #6c757d;
}
.step-item {
    display: flex;
    align-items: center;
    font-weight: 600;
    color: #6c757d;
    font-size: 0.9rem;
}

.step-item.active { color: #007bff; }

.step-blue {
    color: #007bff;
}

.step-icon-blue {
    color: #007bff;
    margin-right: 8px;
    font-size: 1.1rem;
}

.step-dot {
    width: 12px;
    height: 12px;
    background-color: #dee2e6;
    border-radius: 50%;
    margin-right: 8px;
}
.step-dot-blue {
    width: 12px;
    height: 12px;
    background-color: #007bff;
    border-radius: 50%;
    margin-right: 8px;
}

.step-item.active .step-dot { background-color: #DEE2E6; }

.step-line {
    width: 60px;
    height: 2px;
    background-color: #dee2e6;
    margin: 0 15px;
}
.step-line.completed { background-color: #007bff; }

.preview-container {
    width: 100%;
    position: relative;
    min-height: 100px;
    margin: 30px auto;
    padding: 20px;
}

.preview-container.has-pdf {
    min-height: 400px;
}

.preview-wrapper {
    position: relative;
    display: inline-block;
}

.relatorio-preview {
    width: 100%;
    max-width: 500px;
    height: auto;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.pdf-viewer-container {
    width: 100%;
    position: relative;
}

.pdf-file-name {
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 600;
    color: #2d3439;
    word-break: break-all;
}

.loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.95);
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    z-index: 10;
}

.spinner-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.spinner-container p {
    font-size: 1.1rem;
    margin: 0;
}
</style>
